import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      {/* Verified Business Banner */}
      <div className="bg-gradient-to-r from-purple-700 to-indigo-700 text-white text-center py-1 px-4">
        <a 
          href="https://web-techservices.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-xs font-medium hover:underline inline-flex items-center gap-1"
        >
          <svg className="w-3 h-3 text-green-300" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          A Product of Web-Tech Services — Verified Business, Arlington TX
        </a>
      </div>
      <div className="container mx-auto px-4">
        <nav className="flex justify-between items-center h-16">
          <Link to="/">
            <Logo variant="full" size="md" />
          </Link>
          
          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`bg-gray-800 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${mobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`}></span>
              <span className={`bg-gray-800 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${mobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`bg-gray-800 block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${mobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`}></span>
            </div>
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <Link to="/screenplay-guide">
              <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                🎬 Screenplay
              </Button>
            </Link>
            <Link to="/amazon-publishing">
              <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                📚 Novel
              </Button>
            </Link>
            <Link to="/copyright-protection">
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                🛡️ Protect
              </Button>
            </Link>
            <Link to="/kindle-research">
              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 hover:bg-green-50">
                📊 Research
              </Button>
            </Link>
            <Link to="/ghostwriter">
              <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
                ✍️ Ghostwriter
              </Button>
            </Link>
            <Link to="/blog">
              <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                Writer's Block
              </Button>
            </Link>
            <Link to="/club">
              <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                The Exchange
              </Button>
            </Link>
            <Link to="/affiliate">
              <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50">
                Partners
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="ghost" size="sm">Pricing</Button>
            </Link>
            
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm">Dashboard</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  Log Out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Log In</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile navigation overlay */}
          {mobileMenuOpen && (
            <div className="md:hidden fixed inset-0 z-50 bg-white">
              <div className="flex flex-col h-full">
                {/* Mobile header */}
                <div className="flex justify-between items-center p-6 border-b">
                  <Logo variant="full" size="lg" />
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2"
                    aria-label="Close menu"
                  >
                    <div className="w-6 h-6 flex flex-col justify-center items-center">
                      <span className="bg-gray-800 block h-0.5 w-6 rounded-sm rotate-45 translate-y-1"></span>
                      <span className="bg-gray-800 block h-0.5 w-6 rounded-sm -rotate-45 -translate-y-1"></span>
                    </div>
                  </button>
                </div>
                
                {/* Mobile menu items */}
                <div className="flex flex-col gap-4 p-6 overflow-y-auto">
                  <Link 
                    to="/screenplay-guide" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-purple-600 bg-purple-50 rounded-lg"
                  >
                    🎬 <span>Screenplay Guide</span>
                  </Link>
                  <Link 
                    to="/amazon-publishing" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-orange-600 bg-orange-50 rounded-lg"
                  >
                    📚 <span>Amazon Publishing</span>
                  </Link>
                  <Link 
                    to="/copyright-protection" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-red-600 bg-red-50 rounded-lg"
                  >
                    🛡️ <span>Copyright Protection</span>
                  </Link>
                  <Link 
                    to="/kindle-research" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-green-600 bg-green-50 rounded-lg"
                  >
                    📊 <span>Kindle Research Pro</span>
                  </Link>
                  <Link 
                    to="/ghostwriter" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-purple-600 bg-purple-50 rounded-lg"
                  >
                    ✍️ <span>AI Ghostwriter Pro</span>
                  </Link>
                  <Link 
                    to="/blog" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-blue-600 bg-blue-50 rounded-lg"
                  >
                    <span>Writer's Block</span>
                  </Link>
                  <Link 
                    to="/club" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-amber-600 bg-amber-50 rounded-lg"
                  >
                    <span>The Authors' Exchange</span>
                  </Link>
                  <Link 
                    to="/affiliate" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-emerald-600 bg-emerald-50 rounded-lg"
                    data-testid="mobile-nav-partners"
                  >
                    <span>Partner Program</span>
                  </Link>
                  <Link 
                    to="/pricing" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 p-4 text-lg font-medium text-gray-700 bg-gray-50 rounded-lg"
                  >
                    💰 <span>Pricing</span>
                  </Link>
                  
                  {user ? (
                    <>
                      <Link 
                        to="/dashboard" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 p-4 text-lg font-medium text-gray-700 bg-gray-50 rounded-lg"
                      >
                        📊 <span>Dashboard</span>
                      </Link>
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-3 p-4 text-lg font-medium text-gray-700 bg-gray-50 rounded-lg text-left"
                      >
                        🚪 <span>Log Out</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link 
                        to="/login" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 p-4 text-lg font-medium text-gray-700 bg-gray-50 rounded-lg"
                      >
                        🔑 <span>Log In</span>
                      </Link>
                      <Link 
                        to="/register" 
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-3 p-4 text-lg font-medium text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg"
                      >
                        ✨ <span>Sign Up Free</span>
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}
